import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import { supabase, TABLES } from '../../config/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import toast from 'react-hot-toast'

const { FiX, FiSave } = FiIcons

const EditChildModal = ({ isOpen, onClose, child, onChildUpdated }) => {
  const { family } = useAuth()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: child?.name || '',
    age: child?.age || '',
    avatar: child?.avatar || '👦'
  })

  // Reset form data when child changes
  React.useEffect(() => {
    if (child) {
      setFormData({
        name: child.name || '',
        age: child.age || '',
        avatar: child.avatar || '👦'
      })
    }
  }, [child])

  const avatarOptions = [
    // Basic children - various ethnicities
    '👦', '👧', '🧒', '👶',
    
    // Light skin tone
    '👦🏻', '👧🏻', '🧒🏻', '👶🏻',
    
    // Medium-light skin tone
    '👦🏼', '👧🏼', '🧒🏼', '👶🏼',
    
    // Medium skin tone
    '👦🏽', '👧🏽', '🧒🏽', '👶🏽',
    
    // Medium-dark skin tone
    '👦🏾', '👧🏾', '🧒🏾', '👶🏾',
    
    // Dark skin tone
    '👦🏿', '👧🏿', '🧒🏿', '👶🏿',

    // Superheroes - Male (various skin tones)
    '🦸‍♂️', '🦸🏻‍♂️', '🦸🏼‍♂️', '🦸🏽‍♂️', '🦸🏾‍♂️', '🦸🏿‍♂️',
    
    // Superheroes - Female (various skin tones)
    '🦸‍♀️', '🦸🏻‍♀️', '🦸🏼‍♀️', '🦸🏽‍♀️', '🦸🏾‍♀️', '🦸🏿‍♀️',

    // Supervillains - Male (various skin tones)
    '🦹‍♂️', '🦹🏻‍♂️', '🦹🏼‍♂️', '🦹🏽‍♂️', '🦹🏾‍♂️', '🦹🏿‍♂️',
    
    // Supervillains - Female (various skin tones)
    '🦹‍♀️', '🦹🏻‍♀️', '🦹🏼‍♀️', '🦹🏽‍♀️', '🦹🏾‍♀️', '🦹🏿‍♀️',

    // Royalty - various skin tones
    '🤴', '🤴🏻', '🤴🏼', '🤴🏽', '🤴🏾', '🤴🏿',
    '👸', '👸🏻', '👸🏼', '👸🏽', '👸🏾', '👸🏿',

    // Mages/Wizards - various skin tones
    '🧙‍♂️', '🧙🏻‍♂️', '🧙🏼‍♂️', '🧙🏽‍♂️', '🧙🏾‍♂️', '🧙🏿‍♂️',
    '🧙‍♀️', '🧙🏻‍♀️', '🧙🏼‍♀️', '🧙🏽‍♀️', '🧙🏾‍♀️', '🧙🏿‍♀️',

    // Genies - various skin tones
    '🧞‍♂️', '🧞‍♀️',

    // Elves - various skin tones
    '🧝‍♂️', '🧝🏻‍♂️', '🧝🏼‍♂️', '🧝🏽‍♂️', '🧝🏾‍♂️', '🧝🏿‍♂️',
    '🧝‍♀️', '🧝🏻‍♀️', '🧝🏼‍♀️', '🧝🏽‍♀️', '🧝🏾‍♀️', '🧝🏿‍♀️',

    // Fairies - various skin tones
    '🧚‍♂️', '🧚🏻‍♂️', '🧚🏼‍♂️', '🧚🏽‍♂️', '🧚🏾‍♂️', '🧚🏿‍♂️',
    '🧚‍♀️', '🧚🏻‍♀️', '🧚🏼‍♀️', '🧚🏽‍♀️', '🧚🏾‍♀️', '🧚🏿‍♀️',

    // Vampires - various skin tones
    '🧛‍♂️', '🧛🏻‍♂️', '🧛🏼‍♂️', '🧛🏽‍♂️', '🧛🏾‍♂️', '🧛🏿‍♂️',
    '🧛‍♀️', '🧛🏻‍♀️', '🧛🏼‍♀️', '🧛🏽‍♀️', '🧛🏾‍♀️', '🧛🏿‍♀️',

    // Fun characters and animals
    '🦄', '🌟', '🚀', '⭐', '🎨', '🎵', '📚', '⚽', '🏀', '⚾', '🎮', '🎪',
    '🐱', '🐶', '🐻', '🐸', '🦊', '🐰', '🐼', '🐯', '🦁', '🐨', '🐵', '🐧'
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!child || !family) {
      toast.error('Child or family not found. Please refresh and try again.')
      return
    }

    setLoading(true)
    try {
      console.log('Updating child with data:', formData)

      const updatedChildData = {
        name: formData.name.trim(),
        age: formData.age ? parseInt(formData.age) : null,
        avatar: formData.avatar,
        updated_at: new Date().toISOString()
      }

      const { data, error } = await supabase
        .from(TABLES.CHILDREN)
        .update(updatedChildData)
        .eq('id', child.id)
        .eq('family_id', family.id) // Ensure user can only update their family's children
        .select()
        .single()

      if (error) {
        console.error('Child update error:', error)
        toast.error(`Failed to update child: ${error.message || 'Unknown error'}`)
      } else {
        console.log('Child updated successfully:', data)
        toast.success(`${formData.name}'s profile updated successfully! 🎉`)
        onChildUpdated?.(data)
        onClose()
      }
    } catch (error) {
      console.error('Unexpected error updating child:', error)
      toast.error('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  if (!child) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Child Profile</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Current Avatar Preview */}
                <div className="text-center mb-4">
                  <div className="text-6xl mb-2">{formData.avatar}</div>
                  <p className="text-sm text-gray-600">Current Avatar</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Child's Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter child's name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age (Optional)
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    min="1"
                    max="18"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter age"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Choose New Avatar
                  </label>
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    <div className="grid grid-cols-6 gap-2">
                      {avatarOptions.map((avatar, index) => (
                        <button
                          key={`${avatar}-${index}`}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, avatar }))}
                          className={`
                            p-3 rounded-lg border-2 transition-all duration-200 text-2xl hover:scale-110
                            ${formData.avatar === avatar
                              ? 'border-primary-500 bg-primary-50 shadow-md'
                              : 'border-gray-200 hover:border-gray-300'
                            }
                          `}
                        >
                          {avatar}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Scroll to see more avatar options including diverse superheroes, royalty, and magical characters
                  </p>
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    className="flex-1"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={loading}
                    className="flex-1"
                  >
                    <SafeIcon icon={FiSave} className="text-sm" />
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default EditChildModal